import * as React from 'react'
import styled from 'styled-components'
import * as constants from 'static/constants'
import { getOtaPendingStatusLabel } from 'static/constants_ota_pending'
import OtaPendingReservationFilter, {
  IOtaPendingFilterOptions,
} from './OtaPendingReservationFilter'
import SvgIcon from 'components/atoms/SvgIcon'

interface IProps {
  searchOtaPendingReservations: (page: number) => void
  filterOptions: IOtaPendingFilterOptions
  setFilterOptions: React.Dispatch<React.SetStateAction<IOtaPendingFilterOptions>>
}

const DEFAULT_FILTER_OPTIONS: IOtaPendingFilterOptions = {}

const buildFilterLabels = (filterOptions: IOtaPendingFilterOptions) => {
  const labels = []

  if (filterOptions.status) {
    const statusLabel = getOtaPendingStatusLabel(filterOptions.status)
    labels.push({ type: 'status', label: statusLabel })
  }
  if (filterOptions.seller) {
    labels.push({ type: 'seller', label: `Seller: ${filterOptions.seller}` })
  }
  if (filterOptions.tour_date) {
    labels.push({ type: 'date', label: `Tour: ${filterOptions.tour_date}` })
  }
  if (filterOptions.customer_email) {
    labels.push({ type: 'exact', label: `Email: ${filterOptions.customer_email}` })
  }
  if (filterOptions.seller_booking_id) {
    labels.push({ type: 'exact', label: `ID: ${filterOptions.seller_booking_id}` })
  }

  return labels
}

const OtaPendingReservationSearch: React.VFC<IProps> = React.memo(
  ({ searchOtaPendingReservations, filterOptions, setFilterOptions }) => {
    const [showFilter, setShowFilter] = React.useState(false)
    const [filterLabels, setFilterLabels] = React.useState([])

    React.useEffect(() => {
      searchOtaPendingReservations(1)
    }, [filterOptions, searchOtaPendingReservations])

    React.useEffect(() => {
      setFilterLabels(buildFilterLabels(filterOptions))
    }, [filterOptions])

    return (
      <Wrapper>
        <div className="filterContainer">
          <div
            className="button filter"
            onClick={() => setShowFilter(true)}
            role="button"
            aria-label="filtering list"
          >
            {filterLabels.length > 0 ? (
              filterLabels.map((option, index) => (
                <div className={`filterLabel ${option.type}`} key={index}>
                  {option.label}
                </div>
              ))
            ) : (
              <>
                <SvgIcon
                  name="filter_list"
                  className="icon"
                  ariaHidden={true}
                  role="presentation"
                />
                ここから検索
              </>
            )}
          </div>
          {filterLabels.length > 0 && (
            <div
              onClick={() => setFilterOptions(DEFAULT_FILTER_OPTIONS)}
              className="clearFilter"
              role="button"
              aria-label="Clear filtering"
            >
              <SvgIcon name="close" className="icon" ariaHidden={true} role="presentation" />
            </div>
          )}
          {showFilter && (
            <OtaPendingReservationFilter
              filterOptions={filterOptions}
              setFilterOptions={setFilterOptions}
              setShowFilter={setShowFilter}
            />
          )}
        </div>
      </Wrapper>
    )
  }
)

const Wrapper = styled.div`
  .filterContainer {
    position: relative;
    margin: 0 16px 16px;
    pointer-events: auto;
    .clearFilter {
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      top: 50%;
      right: 8px;
      width: 24px;
      height: 24px;
      background-color: ${constants.COLORS.primary};
      border-radius: 50%;
      transform: translateY(-50%);
      transition: background-color 0.2s;
      cursor: pointer;
      &:hover {
        background-color: ${constants.COLORS.secondary};
      }
      .icon {
        color: ${constants.COLORS.white};
        width: 16px;
        height: 16px;
      }
    }
    .button.filter {
      display: flex;
      align-items: center;
      font-size: 14px;
      height: 36px;
      line-height: 36px;
      padding: 0 12px;
      transition: opacity 0.2s;
      border: solid 1px ${constants.COLORS.divider};
      border-radius: 4px;
      padding-right: 44px;
      overflow-x: auto;
      overflow-y: hidden;
      cursor: pointer;
      .icon {
        color: ${constants.COLORS.subText};
        margin-right: 8px;
      }
      .filterLabel {
        position: relative;
        padding: 4px 8px;
        margin-right: 8px;
        height: 28px;
        line-height: 20px;
        font-size: 12px;
        border: 1px solid ${constants.COLORS.divider};
        border-radius: 4px;
        white-space: nowrap;
        &:last-child {
          margin-right: 0;
        }
        &::before {
          content: '';
          position: absolute;
          top: 50%;
          left: -1px;
          width: 4px;
          height: 16px;
          transform: translateY(-50%);
          border-radius: 0 2px 2px 0;
        }
        &.status {
          &::before {
            background-color: ${constants.COLORS.secondary};
          }
        }
        &.seller {
          &::before {
            background-color: #f08a0d;
          }
        }
        &.exact {
          &::before {
            background-color: ${constants.COLORS.primary};
          }
        }
        &.date {
          &::before {
            background-color: #4caf50;
          }
        }
      }
    }
  }
`

export default OtaPendingReservationSearch
